import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import AnalyticsOverview from '../../../components/Admin/AnalyticsOverview';
import EngagementTrends from '../../../components/Admin/EngagementTrends';
import SystemAlertsPanel from '../../../components/Admin/SystemAlertsPanel';
import AdminTasksBoard from '../../../components/Admin/AdminTasksBoard';
import ProgramHealthCheckDashboard from '../../../components/Programs/ProgramHealthCheckDashboard';

import KPIWidgets from '../../../components/AdminDashboard/KPIWidgets';
import ClientFunnelChart from '../../../components/AdminDashboard/ClientFunnelChart';
import ApplicationsTrendChart from '../../../components/AdminDashboard/ApplicationsTrendChart';

import ExportDataAction from '../../../components/Admin/QuickActions/ExportDataAction';
import GenerateReportAction from '../../../components/Admin/QuickActions/GenerateReportAction';
import SettingsAction from '../../../components/Admin/QuickActions/SettingsAction';
import RefreshAction from '../../../components/Admin/QuickActions/RefreshAction';
import { HiShieldCheck, HiUserGroup, HiChartBar } from 'react-icons/hi2';

const SuperAdminHome = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [isLoading, setIsLoading] = useState(false);

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'analytics', label: 'Analytics' },
    { id: 'alerts', label: 'System Alerts' },
    { id: 'tasks', label: 'Tasks' },
    { id: 'programHealth', label: 'Program Health' },
  ];

  const handleTabChange = tabId => {
    setActiveTab(tabId);
    toast.info(`Switched to ${tabs.find(tab => tab.id === tabId)?.label} view`);
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'overview':
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="rounded-xl border border-gray-200 bg-gradient-to-br from-blue-50 to-blue-100 p-6 shadow-sm"
              >
                <div className="flex items-center gap-3">
                  <div className="rounded-lg bg-blue-600 p-3">
                    <HiShieldCheck className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-600">Super Admin Access</p>
                    <p className="text-2xl font-bold text-black">Full Control</p>
                  </div>
                </div>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="rounded-xl border border-gray-200 bg-gradient-to-br from-gray-50 to-gray-100 p-6 shadow-sm"
              >
                <div className="flex items-center gap-3">
                  <div className="rounded-lg bg-gray-600 p-3">
                    <HiUserGroup className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-600">User Management</p>
                    <p className="text-2xl font-bold text-black">Complete</p>
                  </div>
                </div>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="rounded-xl border border-gray-200 bg-gradient-to-br from-green-50 to-green-100 p-6 shadow-sm"
              >
                <div className="flex items-center gap-3">
                  <div className="rounded-lg bg-green-600 p-3">
                    <HiChartBar className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-600">System Analytics</p>
                    <p className="text-2xl font-bold text-black">Advanced</p>
                  </div>
                </div>
              </motion.div>
            </div>
            <AnalyticsOverview />
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              <EngagementTrends />
              <SystemAlertsPanel />
            </div>
          </div>
        );
      case 'analytics':
        return (
          <div className="space-y-6">
            <EngagementTrends />
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              <ClientFunnelChart />
              <ApplicationsTrendChart />
            </div>
          </div>
        );
      case 'alerts':
        return <SystemAlertsPanel />;
      case 'tasks':
        return <AdminTasksBoard />;
      case 'programHealth':
        return <ProgramHealthCheckDashboard userRole="superAdmin" />;
      default:
        return <AnalyticsOverview />;
    }
  };

  return (
    <div className="min-h-screen bg-white p-3 md:p-6">
      <div className="mx-auto max-w-7xl rounded-xl p-4 md:p-6">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6 md:mb-8"
        >
          <div className="bg flex items-center justify-between">
            <div>
              <h1 className="mb-2 text-2xl font-bold text-black md:text-3xl">
                Super Admin Dashboard
              </h1>
              <p className="text-sm text-gray-600">
                Complete system oversight and administrative control
              </p>
            </div>
            <div className="flex items-center gap-2 md:gap-4">
              <div className="text-right"></div>
              <div className="h-1.5 w-1.5 animate-pulse rounded-full bg-blue-600 md:h-2 md:w-2"></div>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-6 md:mb-8"
        >
          <div className="flex items-center gap-1 overflow-x-auto rounded-xl border border-gray-200 bg-white p-1 shadow-sm">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={`flex items-center gap-2 whitespace-nowrap rounded-lg px-3 py-1.5 text-xs font-medium transition-all duration-200 md:px-4 md:py-2 md:text-sm ${
                  activeTab === tab.id
                    ? 'border-b-4 border-blue-600 bg-blue-600 text-white shadow-md'
                    : 'text-gray-800 hover:bg-gray-100 hover:text-black'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </motion.div>

        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="space-y-4 md:space-y-6"
        >
          {renderContent()}
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="mt-8 border-t border-gray-200 pt-6 md:mt-12 md:pt-8"
        >
          <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm md:p-6">
            <h3 className="mb-3 text-base font-semibold text-black md:mb-4 md:text-lg">
              Quick Actions
            </h3>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4 md:gap-4">
              <ExportDataAction />
              <GenerateReportAction />
              <SettingsAction />
              <RefreshAction />
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default SuperAdminHome;
